import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import Cropper from "react-easy-crop";
import { apiClient, imageBaseUrl } from "../utils/const";

const EditCustomer = ({ customerId, onClose }) => {
  const [schemeType, setSchemeType] = useState(localStorage.getItem("schemeType")?.toLowerCase());

  // Listen for scheme changes
  useEffect(() => {
    const handleSchemeChange = (event) => {
      const newScheme = event.detail.to?.toLowerCase();
      setSchemeType(newScheme);
    };

    window.addEventListener("schemeChanged", handleSchemeChange);
    return () => window.removeEventListener("schemeChanged", handleSchemeChange);
  }, []);
  const id = customerId;

  // ---------- Form state ----------
  const [formData, setFormData] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    phone: "",
    email: "",
    card_no: "",
    monthly_amount: "",
    bonus: "",
    address: "",
    scheme_id: "",
    pan_card: null,
    aadhar_front: null,
    aadhar_back: null,
  });

  // ---------- Preview URLs ----------
  const [previews, setPreviews] = useState({
    pan_card: null,
    aadhar_front: null,
    aadhar_back: null,
  });

  // ---------- Cropper state ----------
  const [cropConfig, setCropConfig] = useState({
    show: false,
    field: "",
    image: null,
    crop: { x: 0, y: 0 },
    zoom: 1,
    croppedAreaPixels: null,
    aspect: 1.6,
    originalPreview: null,
  });

  // ---------- Enlarged image state ----------
  const [enlargedImage, setEnlargedImage] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ====== Helpers: cropper ======
  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCropConfig((prev) => ({ ...prev, croppedAreaPixels }));
  }, []);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });

  const getCroppedImg = async (imageSrc, cropped) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = Math.round(cropped.width);
    canvas.height = Math.round(cropped.height);

    ctx.drawImage(
      image,
      cropped.x,
      cropped.y,
      cropped.width,
      cropped.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const file = new File([blob], "upload.jpg", { type: "image/jpeg" });
        resolve({ file, url: URL.createObjectURL(blob) });
      }, "image/jpeg", 0.92);
    });
  };

  const resetCropper = () => {
    // Restore old preview if cancel
    if (cropConfig.originalPreview) {
      setPreviews((prev) => ({
        ...prev,
        [cropConfig.field]: cropConfig.originalPreview,
      }));
    }

    if (cropConfig.image) URL.revokeObjectURL(cropConfig.image);

    setCropConfig({
      show: false,
      field: "",
      image: null,
      crop: { x: 0, y: 0 },
      zoom: 1,
      croppedAreaPixels: null,
      aspect: 1.6,
      originalPreview: null,
    });
  };

  const handleImageChange = (field, file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);

    setCropConfig({
      show: true,
      field,
      image: url,
      crop: { x: 0, y: 0 },
      zoom: 1,
      croppedAreaPixels: null,
      aspect: 1.6,
      originalPreview: previews[field],
    });
  };

  const handleCropSave = async () => {
    if (!cropConfig.croppedAreaPixels) return;

    const { file, url } = await getCroppedImg(
      cropConfig.image,
      cropConfig.croppedAreaPixels
    );

    setFormData((prev) => ({ ...prev, [cropConfig.field]: file }));

    setPreviews((prev) => {
      const old = prev[cropConfig.field];
      if (old && old.startsWith("blob:")) URL.revokeObjectURL(old);
      return { ...prev, [cropConfig.field]: url };
    });

    if (cropConfig.image) URL.revokeObjectURL(cropConfig.image);
    setCropConfig((prev) => ({ ...prev, show: false, image: null, originalPreview: null }));
  };

  // ====== Helpers: scheme mapping ======
  const handleSchemeChange = (value) => {
    let id = "";
    const normalized = String(value || "").toLowerCase();
    if (
      normalized === "1" ||
      normalized.includes("(9+1)") ||
      normalized.includes("scheme-1")
    ) {
      id = "1";
    } else if (
      normalized === "2" ||
      normalized.includes("(12+2)") ||
      normalized.includes("scheme-2")
    ) {
      id = "2";
    }
    setFormData((prev) => ({ ...prev, scheme_id: id }));
  };

  // ====== Fetch & prefill user data ======
  useEffect(() => {
    const endpoint =
      schemeType === "kanchan" ? "/kanchan/kanchan_users_data.php" : "/mandal/mandal_users_data.php";

    apiClient
      .post(endpoint)
      .then((res) => {
        const data = res.data;
        const users = data.data?.users || [];
        if (Array.isArray(users)) {
          const user = users.find((u) => String(u.id) === String(id));
          if (!user) {
            setError("Customer not found.");
            setLoading(false);
            return;
          }

          const fullName = user.full_name || user.name || "";
          const parts =
            user.first_name && user.last_name
              ? [user.first_name, user.middle_name || "", user.last_name]
              : fullName
                ? fullName.trim().split(/\s+/)
                : [];

          const first = user.first_name || parts[0] || "";
          const last =
            user.last_name || (parts.length > 1 ? parts[parts.length - 1] : "");
          let middle = "";
          if (user.middle_name) middle = user.middle_name;
          else if (parts.length > 2)
            middle = parts.slice(1, parts.length - 1).join(" ");
          else if (parts.length === 2) middle = "";

          const normalizeSchemeId = (value, name) => {
            if (!value && !name) return "";
            const source = (value ? String(value) : String(name || "")).toLowerCase();
            if (
              source === "1" ||
              source.includes("(9+1)") ||
              source.includes("scheme-1")
            )
              return "1";
            if (
              source === "2" ||
              source.includes("(12+2)") ||
              source.includes("scheme-2")
            )
              return "2";
            return "";
          };

          const initial = {
            first_name: first,
            middle_name: middle,
            last_name: last,
            phone: user.phone || "",
            email: user.email || "",
            card_no: user.card_no || "",
            monthly_amount: user.monthly_amount ?? "",
            bonus:
              schemeType === "mandal"
                ? user.bonus ?? user.monthly_amount ?? ""
                : "",
            address: user.address ?? "",
            scheme_id: normalizeSchemeId(user.scheme_id, user.scheme_name),
            pan_card: null,
            aadhar_front: null,
            aadhar_back: null,
          };

          setFormData(initial);

          const resolveUrl = (path) => {
            if (!path) return null;
            if (path.startsWith("http")) return path;
            const filePart = path.includes("/") ? path : encodeURIComponent(path);
            return `${imageBaseUrl}/${filePart}`;
          };

          const panUrl = resolveUrl(user.pan_card_url || user.pan_card);
          const aadharFrontUrl = resolveUrl(user.aadhar_front_url || user.aadhar_front);
          const aadharBackUrl = resolveUrl(user.aadhar_back_url || user.aadhar_back);


          setPreviews({
            pan_card: panUrl,
            aadhar_front: aadharFrontUrl,
            aadhar_back: aadharBackUrl,
          });

          setLoading(false);
        } else {
          setError("Invalid user list response.");
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("User list fetch error:", err);
        setError("Network error.");
        setLoading(false);
      });
  }, [id, schemeType]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (cropConfig.image) URL.revokeObjectURL(cropConfig.image);
      Object.values(previews).forEach((u) => {
        if (u && typeof u === "string" && u.startsWith("blob:")) {
          URL.revokeObjectURL(u);
        }
      });
    };
  }, [cropConfig.image, previews]);

  // ====== Enlarged image handlers ======
  const openEnlargedImage = (url) => setEnlargedImage(url);
  const closeEnlargedImage = () => setEnlargedImage(null);

  // ====== Submit ======
  const handleSubmit = async (e) => {
    e.preventDefault();

    const apiUrl =
      schemeType === "mandal"
        ? "mandal/mandal_update_users.php"
        : "kanchan/kanchan_update_users.php";

    try {
      const payload = new FormData();
      payload.append("id", id);
      payload.append("first_name", formData.first_name);
      payload.append("middle_name", formData.middle_name || "");
      payload.append("last_name", formData.last_name);
      payload.append("phone", formData.phone);
      payload.append("email", formData.email);
      payload.append("card_no", formData.card_no || "");

      if (schemeType === "mandal") {
        payload.append("scheme_type", "Mandal");
        payload.append("monthly_amount", formData.monthly_amount || "");
        payload.append("bonus", formData.bonus || "");
        payload.append("address", formData.address || "");
      }

      if (schemeType === "kanchan") {
        payload.append("scheme_id", formData.scheme_id || "");
        if (formData.pan_card instanceof File)
          payload.append("pan_card", formData.pan_card);
        if (formData.aadhar_front instanceof File)
          payload.append("aadhar_front", formData.aadhar_front);
        if (formData.aadhar_back instanceof File)
          payload.append("aadhar_back", formData.aadhar_back);
      }

      const res = await apiClient.post(apiUrl, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const result = res.data;
      // console.log(result);
      if (result?.message === 'radha') {
        toast.success("Customer updated successfully!");
        onClose?.();
      } else {
        toast.error("Error updating customer");
      }
    } catch (err) {
      toast.error("Request failed: " + err.message);
    }
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="alert alert-danger m-3">{error}</div>;

  return (
    <>
      {/* Cropper Modal */}
      {cropConfig.show && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(0,0,0,0.7)", zIndex: 9999 }}
        >
          <div
            className="bg-white p-3 rounded-3 shadow"
            style={{ width: "90%", maxWidth: 640, position: "relative" }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                height: 360,
                overflow: "hidden",
                borderRadius: "0.5rem",
              }}
            >
              <Cropper
                image={cropConfig.image}
                crop={cropConfig.crop}
                zoom={cropConfig.zoom}
                aspect={cropConfig.aspect}
                onCropChange={(crop) =>
                  setCropConfig((prev) => ({ ...prev, crop }))
                }
                onZoomChange={(zoom) =>
                  setCropConfig((prev) => ({ ...prev, zoom }))
                }
                onCropComplete={onCropComplete}
                restrictPosition={false}
                showGrid
              />
            </div>

            <div className="d-flex align-items-center justify-content-end gap-2 mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCropSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enlarged Image Modal */}
      {enlargedImage && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            background: "rgba(0,0,0,0.8)",
            zIndex: 10000,
            cursor: "zoom-out",
          }}
          onClick={closeEnlargedImage}
        >
          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeEnlargedImage();
            }}
            className="position-absolute"
            style={{
              top: "20px",
              right: "20px",
              background: "rgba(255,255,255,0.9)",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: "20px",
              fontWeight: "bold",
              zIndex: 10001,
            }}
            title="Close"
          >
            ×
          </button>
          
          {/* Image Container */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "90%",
              maxHeight: "90%",
            }}
          >
            <img
              src={enlargedImage}
              alt="Enlarged"
              onError={(e) => {
                console.error("Failed to load image:", enlargedImage);
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "block";
              }}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                borderRadius: "0.5rem",
                boxShadow: "0 0 15px rgba(0,0,0,0.5)",
              }}
            />
            {/* Error fallback */}
            <div
              style={{
                display: "none",
                background: "white",
                padding: "20px",
                borderRadius: "0.5rem",
                textAlign: "center",
                color: "#666",
              }}
            >
              <i className="fas fa-image" style={{ fontSize: "48px", marginBottom: "10px" }}></i>
              <p>Image could not be loaded</p>
              <small>{enlargedImage}</small>
            </div>
          </div>
        </div>
      )}

      {/* ---- Main Form ---- */}
      <form onSubmit={handleSubmit} autoComplete="off">
        <div className="row g-3">
          {/* Name Fields */}
          <div className="col-md-4">
            <label className="form-label">
              First Name <span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              value={formData.first_name}
              onChange={(e) =>
                setFormData((p) => ({ ...p, first_name: e.target.value }))
              }
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Middle Name (Parent's Name)</label>
            <input
              className="form-control"
              value={formData.middle_name}
              onChange={(e) =>
                setFormData((p) => ({ ...p, middle_name: e.target.value }))
              }
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">
              Last Name <span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              value={formData.last_name}
              onChange={(e) =>
                setFormData((p) => ({ ...p, last_name: e.target.value }))
              }
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">
              Card No <span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              value={formData.card_no}
              onChange={(e) =>
                setFormData((p) => ({ ...p, card_no: e.target.value }))
              }
            />
          </div>

          {/* Mandal form */}
          {schemeType === "mandal" && (
            <>
              <div className="col-md-4">
                <label className="form-label">
                  Phone <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, phone: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Email</label>
                <input
                  className="form-control"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, email: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-4 d-flex flex-column">
                <label className="form-label">Address</label>
                <textarea
                  className="form-control"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, address: e.target.value }))
                  }
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Monthly Amount <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.monthly_amount}
                  onChange={(e) => {
                    const amount = e.target.value;
                    setFormData((p) => ({
                      ...p,
                      monthly_amount: amount,
                      bonus: amount,
                    }));
                  }}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Bonus</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.bonus}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, bonus: e.target.value }))
                  }
                />
              </div>
            </>
          )}

          {/* Kanchan form */}
          {schemeType === "kanchan" && (
            <>
              <div className="col-md-4">
                <label className="form-label">
                  Phone <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, phone: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Email</label>
                <input
                  className="form-control"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, email: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Scheme <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={formData.scheme_id}
                  onChange={(e) => handleSchemeChange(e.target.value)}
                  required
                >
                  <option value="">-- Select Scheme --</option>
                  <option value="1">(9+1)Gold Scheme</option>
                  <option value="2">(12+2)Gold Scheme</option>
                </select>
              </div>

              <div className="col-12" />

              <div className="col-md-4">
                <label className="form-label">PAN Card</label>
                <input
                  className="form-control"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) =>
                    handleImageChange("pan_card", e.target.files[0])
                  }
                />
                {previews.pan_card && (
                  <img
                    src={previews.pan_card}
                    alt="PAN Preview"
                    className="img-fluid mt-2 border rounded"
                    style={{ cursor: "pointer" }}
                    onClick={() => setEnlargedImage(previews.pan_card)}
                  />
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">Aadhaar Card (Front)</label>
                <input
                  className="form-control"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) =>
                    handleImageChange("aadhar_front", e.target.files[0])
                  }
                />
                {previews.aadhar_front && (
                  <img
                    src={previews.aadhar_front}
                    alt="Aadhaar Front Preview"
                    className="img-fluid mt-2 border rounded"
                    style={{ cursor: "pointer" }}
                    onClick={() => setEnlargedImage(previews.aadhar_front)}
                  />
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">Aadhaar Card (Back)</label>
                <input
                  className="form-control"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) =>
                    handleImageChange("aadhar_back", e.target.files[0])
                  }
                />
                {previews.aadhar_back && (
                  <img
                    src={previews.aadhar_back}
                    alt="Aadhaar Back Preview"
                    className="img-fluid mt-2 border rounded"
                    style={{ cursor: "pointer" }}
                    onClick={() => setEnlargedImage(previews.aadhar_back)}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="mt-3 d-flex justify-content-end gap-2">
          <button type="submit" className="btn btn-primary">
            Save
          </button>
        </div>
      </form>
    </>
  );
};

export default EditCustomer;
